import React, {memo, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import {
    contentMaxWidth,
    colors,
    fontSize,
    fontWeight,
    lineHeight,
    letterSpacing,
    borderRadius,
    overlay,
    spacing,
    decorSize,
    borderWidth,
    shadows
} from '@/styles/tokens';
import {useCountUp} from '@/hooks/useCountUp';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import ArrowForward from '@mui/icons-material/ArrowForward';
import type {ctaContent} from '@/util/contents/drawpost-data';

interface CTASectionProps {
    openForm: () => void;
    heading: string;
    subheading: string;
    content: typeof ctaContent;
    className?: string;
}

const sectionSx = {
    bgcolor: colors.accent.indigo,
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'center'
} as const;

const orbSx = {
    position: 'absolute',
    width: decorSize.orbLg,
    height: decorSize.orbLg,
    borderRadius: borderRadius.circle,
    bgcolor: overlay.whiteTenth,
    filter: `blur(${decorSize.blur})`,
    pointerEvents: 'none'
} as const;

const orbOneSx = {...orbSx, left: '0%', top: '-30%'} as const;
const orbTwoSx = {...orbSx, right: '0%', bottom: '-30%', opacity: 0.6} as const;

const innerSx = {
    position: 'relative',
    maxWidth: contentMaxWidth.standard,
    mx: 'auto',
    px: {xs: 3, md: 4}
} as const;

const pillBadgeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: spacing[24],
    py: spacing[12],
    borderRadius: borderRadius.full,
    bgcolor: overlay.whiteTenth,
    border: `${borderWidth.thin} solid ${overlay.whiteLight}`,
    color: colors.text.inverse,
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.slight,
    textTransform: 'uppercase' as const,
    mb: 1
} as const;

const sparkleIconSx = {
    fontSize: fontSize[20],
    color: colors.accent.gold
} as const;

const headingSx = {
    color: colors.text.inverse,
    lineHeight: {xs: lineHeight[48], lg: lineHeight[56]},
    letterSpacing: letterSpacing.normal,
    maxWidth: contentMaxWidth.standard,
    mx: 'auto'
} as const;

const subTextSx = {
    color: colors.text.inverse,
    fontSize: fontSize[18],
    fontWeight: fontWeight.regular,
    lineHeight: {xs: lineHeight[28], md: lineHeight[40]},
    letterSpacing: letterSpacing.normal,
    maxWidth: contentMaxWidth.narrow,
    mx: 'auto',
    mb: 6
} as const;

const buttonRowSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[24],
    justifyContent: 'center',
    mb: 6
} as const;

const primaryBtnSx = {
    bgcolor: colors.text.inverse,
    color: `${colors.accent.indigo} !important`,
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    letterSpacing: letterSpacing.snug,
    px: spacing[32],
    py: spacing[20],
    borderRadius: borderRadius.xl,
    textTransform: 'none',
    gap: 1,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease',
    '&:hover': {
        bgcolor: colors.background.subtle,
        transform: 'translateY(-3px)',
        boxShadow: shadows.awardCardElevated
    }
} as const;

const outlineIconSx = {
    fontSize: fontSize[24]
} as const;

const bottomDividerSx = {
    borderTop: `${borderWidth.thin} solid ${overlay.whiteSubtle}`,
    pt: 6
} as const;

const statsRowSx = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: {xs: spacing[32], md: spacing[64]}
} as const;

const statItemSx = {
    textAlign: 'center',
    minWidth: decorSize.statMin
} as const;

const statValueSx = {
    color: colors.text.inverse,
    fontSize: fontSize[36],
    fontWeight: fontWeight.bold,
    lineHeight: lineHeight[40],
    letterSpacing: letterSpacing.slight,
    mb: 1
} as const;

const statLabelSx = {
    color: colors.neutral.slate400,
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[20],
    letterSpacing: letterSpacing.normal
} as const;

// Parse stat string into prefix, number, suffix e.g. "500+" → {prefix:"", value:500, suffix:"+"}
function parseStat(stat: string): {prefix: string; value: number; suffix: string; decimals: number} {
    const match = stat.match(/^(\$?)(\d+\.?\d*)(.*)$/);
    if (!match) return {prefix: '', value: 0, suffix: stat, decimals: 0};
    const raw = match[2];
    const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
    return {prefix: match[1], value: parseFloat(raw), suffix: match[3], decimals};
}

const StatItem = memo(function StatItem({value, label}: {value: string; label: string}) {
    const ref = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    const {prefix, value: target, suffix, decimals} = parseStat(value);
    const count = useCountUp(target, decimals, active);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setActive(true);
                    observer.disconnect();
                }
            },
            {threshold: 0.4}
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Box ref={ref} sx={statItemSx}>
            <Typography sx={statValueSx}>
                {prefix}
                {decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}
                {suffix}
            </Typography>
            <Typography sx={statLabelSx}>{label}</Typography>
        </Box>
    );
});

const CTASection = memo(function CTASection({openForm, heading, subheading, content, className}: CTASectionProps) {
    return (
        <Box sx={sectionSx} className={className}>
            <Box sx={orbOneSx} aria-hidden="true" />
            <Box sx={orbTwoSx} aria-hidden="true" />

            <Box sx={innerSx}>
                <Box sx={pillBadgeSx}>
                    <AutoAwesomeOutlined sx={sparkleIconSx} />
                    {content.pillLabel}
                </Box>

                <Typography variant="h1" component="p" fontSize={{xs: 35, sm: 48}} sx={headingSx} className="font-headers">
                    {heading}
                </Typography>

                <Typography sx={subTextSx} className="font-regular-text remove-underline">
                    {subheading}
                </Typography>

                <Box sx={buttonRowSx}>
                    <Button
                        className="animated-button"
                        sx={primaryBtnSx}
                        onClick={openForm}
                        disableRipple={false}
                        endIcon={<ArrowForward sx={outlineIconSx} />}
                    >
                        {content.ctaLabel}
                    </Button>
                </Box>

                <Box sx={bottomDividerSx}>
                    <Box sx={statsRowSx}>
                        {content.bottomStats.map(({value, label}) => (
                            <StatItem key={label} value={value} label={label} />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
});

export default CTASection;
